import type { LlmUIMessageParts } from '@azymmetric/shared/types/llm.types';
import { Button } from '@components/ui/button';
import { useArtifactStore } from '@stores/artifact.store';
import { BarChart3, Eye } from 'lucide-react';
import { MessageContent } from '../../../../ai-elements/message';
import { BlueLabel } from '../../../../shared/blue-label.component';
import { Spinner } from '../../../../shared/spinner.component';

export type RenderSpaceForceReadinessReportToolProps = {
  messageId: string;
  part: Extract<LlmUIMessageParts, { type: 'tool-renderSpaceForceReadinessReport' }>;
  partIndex: number;
};

export const RenderSpaceForceReadinessReportTool: React.FC<RenderSpaceForceReadinessReportToolProps> = (props) => {
  const { messageId, part, partIndex } = props;
  const { setArtifact } = useArtifactStore();

  if (part.state === 'output-available' && 'output' in part) {
    const report = part.output;

    return (
      <MessageContent key={`${messageId}-${partIndex}`}>
        <div className="rounded-lg border bg-card p-4">
          <div className="flex flex-col gap-3">
            <div className="flex items-start gap-2">
              <div className="rounded-md bg-[#3D2EFF1A] p-2 text-[#3D2EFF]">
                <BarChart3 className="size-4" />
              </div>
              <div>
                <div className="text-sm font-medium">{report.meta.title}</div>
                <BlueLabel text="Space Force Readiness Report" />
              </div>
            </div>
            <div className="text-xs text-muted-foreground">
              {report.meta.cycle} · {report.meta.applicants} applicants · {report.meta.syntheticDataLabel}
            </div>
            <div className="flex justify-end">
              <Button
                onClick={() => {
                  setArtifact({
                    content: report,
                    kind: 'spaceForceReadinessReport',
                    status: 'idle',
                    title: report.meta.title,
                  });
                }}
                size="sm"
                variant="primary"
              >
                <Eye className="mr-1 size-3" />
                View
              </Button>
            </div>
          </div>
        </div>
      </MessageContent>
    );
  }

  if (part.state === 'output-error' && 'errorText' in part) {
    return (
      <MessageContent key={`${messageId}-${partIndex}`}>
        <div className="rounded-lg border border-destructive bg-destructive/10 p-4">
          <div className="text-sm text-destructive">
            Failed to render Space Force readiness report: {part.errorText}
          </div>
        </div>
      </MessageContent>
    );
  }

  if (part.state === 'input-streaming' || part.state === 'input-available') {
    return (
      <MessageContent key={`${messageId}-${partIndex}`}>
        <div className="rounded-lg border bg-card p-4">
          <div className="flex items-center gap-2">
            <Spinner size="sm" />
            <span className="text-sm text-muted-foreground">Rendering Space Force readiness report...</span>
          </div>
        </div>
      </MessageContent>
    );
  }

  return null;
};
